"use client";

import { useSearchParams } from "next/navigation";
import { useState, useEffect, useCallback, useMemo } from "react";
import Button from "~/components/buttons/Button";
import Flex from "~/components/flex/Flex";
import { Icon, IconName } from "~/components/icons/Icon";
import { TextInput } from "~/components/inputs/TextInput";
import { NumberInput } from "~/components/inputs/NumberInput";
import Dropdown from "~/components/inputs/Dropdown";
import DatePickerButton from "~/components/datePicker/DatePickerButton";
import Modal from "~/components/modal/Modal";
import Text from "~/components/texts/Text";
import { SEARCH_PARAM_UPDATE_SINGLE_OFFER_MODAL } from "~/constants";
import { INCOTERM_OPTIONS } from "~/constants/pqIngestionConstants";
import { OFFER_UPDATED } from "~/constants/queryParams";
import {
  AvailabilityTypeEnum,
  ButtonTypesEnum,
  PriceUnitEnum,
  ProductCategory,
  UnitTypeEnum,
} from "~/types-and-enums/generalTypes";
import { PQDetailResponse } from "~/types-and-enums/pqIngestionTypes";
import { updateSearchParams } from "~/utils/clientUtils";
import { colors, spacing } from "~/utils/tailwindUtils";
import { fetchPQById, updateSingleOffering } from "../actions";
import CollapsibleSection from "~/components/collapsible-section/CollapsibleSection";
import ProductImage from "~/components/product-image/ProductImage";
import HorizontalLine from "~/components/horizontalLine/HorizontalLine";
import VerticalLine from "~/components/vertical-line/VerticalLine";
import IncotermLocationSelect from "~/components/incoterm-location-select/IncotermLocationSelect";
import { SEND_INCOTERM_LOCATION_ID } from "~/constants/incotermLocationConstants";
import { SelectedIncotermLocation } from "~/types-and-enums/incotermLocationTypes";
import { incotermLocationFromFields } from "~/utils/incotermLocationUtils";
import Switch from "~/components/switch/NewSwitch";
import { useI18n } from "~/locales/client";
import { formatDateToYYYYMMDD, parseDateFromYYYYMMDD } from "~/utils/dateUtils";
import { inferAvailableQuantityFromMax, inferMoqFromMin } from "~/utils/pqIngestionUtils";
import AndercoreLogoLoadingGif from "~/components/loader/AndercoreLogoLoadingGif";
import { twMerge } from "tailwind-merge";
import { usePostHog } from "~/hooks/usePostHog";

const UpdateSingleOfferModal = () => {
  const searchParams = useSearchParams();
  const { captureEvent } = usePostHog();
  const translate = useI18n();
  const pqId = searchParams.get(SEARCH_PARAM_UPDATE_SINGLE_OFFER_MODAL);

  const [data, setData] = useState<PQDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateShakeTrigger, setUpdateShakeTrigger] = useState(0);

  // Form state
  const [availableQuantity, setAvailableQuantity] = useState("");
  const [availableQuantityUnit, setAvailableQuantityUnit] = useState<UnitTypeEnum>(UnitTypeEnum.pieces);
  const [availabilityType, setAvailabilityType] = useState<AvailabilityTypeEnum>(AvailabilityTypeEnum.IN_STOCK_NOW);
  const [availableFromDate, setAvailableFromDate] = useState<Date | null>(null);
  const [leadTime, setLeadTime] = useState("");
  const [moq, setMoq] = useState("");
  const [moqUnit, setMoqUnit] = useState<UnitTypeEnum>(UnitTypeEnum.pieces);
  const [price, setPrice] = useState("");
  const [priceUnit, setPriceUnit] = useState<PriceUnitEnum>(PriceUnitEnum.pieces);
  const [incoterm, setIncoterm] = useState("");
  const [incotermLocation, setIncotermLocation] = useState<SelectedIncotermLocation | null>(null);
  const [includedLogisticCost, setIncludedLogisticCost] = useState(false);
  const [validUntilDate, setValidUntilDate] = useState<Date | null>(null);

  const [fieldErrors, setFieldErrors] = useState<{
    availableQuantity?: boolean;
    availableFromDate?: boolean;
    leadTime?: boolean;
    moq?: boolean;
    moqExceedsQuantity?: boolean;
    price?: boolean;
    validUntilDate?: boolean;
    incoterm?: boolean;
    incotermLocation?: boolean;
  }>({});

  const shouldOpen = !!pqId;

  const getRequiredFieldError = (label: string) =>
    `${label} ${translate("single-product-offering.is-required")}`;

  // Fetch data
  useEffect(() => {
    if (!pqId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        const result = await fetchPQById(pqId);
        if (cancelled) return;
        if (result.success && result.data) {
          setData(result.data);
        } else {
          setError(result.error || translate("single-product-offering.error.create-failed"));
        }
      } catch (err) {
        if (cancelled) return;
        setError(translate("single-product-offering.error.create-failed"));
        console.error("Error fetching PQ detail:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- translate omitted to prevent infinite loop
  }, [pqId]);

  // Populate form when data loads
  useEffect(() => {
    if (!data) return;

    const av = inferAvailableQuantityFromMax(
      data.maximum_available_quantity,
      data.units_per_container,
      data.units_per_pallet
    );
    const moqInferred = inferMoqFromMin(
      data.minimum_order_quantity,
      data.units_per_container,
      data.units_per_pallet
    );

    setAvailableQuantityUnit(av.unit);
    setAvailableQuantity(av.quantity);
    setAvailabilityType((data.pq_type as AvailabilityTypeEnum) || AvailabilityTypeEnum.IN_STOCK_NOW);
    setAvailableFromDate(
      data.pq_type === AvailabilityTypeEnum.IN_STOCK_NOW
        ? data.earliest_availability_date
          ? parseDateFromYYYYMMDD(data.earliest_availability_date)
          : new Date()
        : data.earliest_availability_date
          ? parseDateFromYYYYMMDD(data.earliest_availability_date)
          : null
    );
    setLeadTime(data.lead_time != null ? String(data.lead_time) : "");
    setMoqUnit(moqInferred.unit);
    setMoq(moqInferred.quantity);
    setPriceUnit(
      data.category === ProductCategory.Modules && data.price_per_watt != null && data.price_per_watt > 0
        ? PriceUnitEnum.wattpeak
        : PriceUnitEnum.pieces
    );
    setPrice(
      data.price_per_watt != null && data.price_per_watt > 0
        ? String(data.price_per_watt)
        : String(data.unit_purchase_price)
    );
    setIncoterm(data.incoterm || "");
    // Linked when the PQ has an incoterm_location_id, otherwise a legacy (free-text) location — or null
    setIncotermLocation(
      incotermLocationFromFields({
        id: data.incoterm_location_id,
        street: data.incoterm_location_street,
        postal_code: data.incoterm_location_postal_code,
        city: data.incoterm_location_city,
        country: data.incoterm_location_country,
      })
    );
    setIncludedLogisticCost(data.includes_logistics_cost ?? false);
    setValidUntilDate(parseDateFromYYYYMMDD(data.valid_until));
  }, [data]);

  // Build patch payload with only changed fields, and detect if there are changes
  const { patchPayload, hasChanges } = useMemo(() => {
    if (!data) return { patchPayload: {} as Record<string, unknown>, hasChanges: false };

    const up = data.units_per_pallet ?? 0;
    const uc = data.units_per_container ?? 0;
    const quantityNum = parseInt(availableQuantity, 10);
    const moqNum = parseInt(moq, 10);
    const priceNum = parseFloat(price);

    const maxAvail =
      availableQuantityUnit === UnitTypeEnum.pallets
        ? up * quantityNum
        : availableQuantityUnit === UnitTypeEnum.containers
          ? uc * quantityNum
          : quantityNum;
    const minOrder =
      moqUnit === UnitTypeEnum.pallets
        ? moqNum * up
        : moqUnit === UnitTypeEnum.containers
          ? moqNum * uc
          : moqNum;

    const payload: Record<string, unknown> = {};

    const newValidUntil = validUntilDate ? formatDateToYYYYMMDD(validUntilDate) : null;
    if (newValidUntil && newValidUntil !== data.valid_until) payload.valid_until = newValidUntil;

    if (incoterm !== (data.incoterm || "")) payload.incoterm = incoterm;

    if (includedLogisticCost !== (data.includes_logistics_cost ?? false)) {
      payload.includes_logistics_cost = includedLogisticCost;
    }

    if (!isNaN(minOrder) && minOrder !== data.minimum_order_quantity) {
      payload.minimum_order_quantity = minOrder;
    }
    if (!isNaN(maxAvail) && maxAvail !== data.maximum_available_quantity) {
      payload.maximum_available_quantity = maxAvail;
    }

    if (availabilityType !== (data.pq_type || AvailabilityTypeEnum.IN_STOCK_NOW)) {
      payload.pq_type = availabilityType;
    }

    if (priceUnit === PriceUnitEnum.pieces && !isNaN(priceNum) && priceNum !== data.unit_purchase_price) {
      payload.unit_purchase_price = priceNum;
    }
    if (priceUnit === PriceUnitEnum.wattpeak && !isNaN(priceNum) && priceNum !== (data.price_per_watt ?? null)) {
      payload.price_per_watt = priceNum;
    }

    const newEarliest =
      availabilityType !== AvailabilityTypeEnum.ALWAYS_IN_STOCK && availableFromDate
        ? formatDateToYYYYMMDD(availableFromDate)
        : null;
    const initEarliest = data.earliest_availability_date ?? null;
    if (newEarliest !== initEarliest) payload.earliest_availability_date = newEarliest;

    const newLeadTime =
      leadTime.trim() !== "" && !isNaN(parseInt(leadTime, 10)) && parseInt(leadTime, 10) >= 0
        ? parseInt(leadTime, 10)
        : 0;
    const initLeadTime = data.lead_time ?? 0;
    if (newLeadTime !== initLeadTime) payload.lead_time = newLeadTime;

    if (
      incotermLocation?.id &&
      incotermLocation.id !== (data.incoterm_location_id ?? null) &&
      SEND_INCOTERM_LOCATION_ID
    ) {
      payload.incoterm_location_id = incotermLocation.id;
    }
    // Compared trimmed (and the country case-insensitively, a legacy "de" is prefilled as "DE"): an untouched
    // prefilled location must not count as a change
    const street = incotermLocation?.street ?? "";
    const postal = incotermLocation?.postal_code ?? "";
    const city = incotermLocation?.city ?? "";
    const country = incotermLocation?.country_code || incotermLocation?.country || "";
    if (street !== (data.incoterm_location_street ?? "").trim()) payload.incoterm_location_street = street;
    if (postal !== (data.incoterm_location_postal_code ?? "").trim()) payload.incoterm_location_postal_code = postal;
    if (city !== (data.incoterm_location_city ?? "").trim()) payload.incoterm_location_city = city;
    if (country.toUpperCase() !== (data.incoterm_location_country ?? "").trim().toUpperCase()) {
      payload.incoterm_location_country = country;
    }

    return { patchPayload: payload, hasChanges: Object.keys(payload).length > 0 };
  }, [
    data,
    availableQuantity,
    availableQuantityUnit,
    availabilityType,
    availableFromDate,
    leadTime,
    moq,
    moqUnit,
    price,
    priceUnit,
    incoterm,
    incotermLocation,
    includedLogisticCost,
    validUntilDate,
  ]);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    const params = new URLSearchParams(searchParams);
    params.delete(SEARCH_PARAM_UPDATE_SINGLE_OFFER_MODAL);
    updateSearchParams(params);
    setData(null);
    setError(null);
    setFieldErrors({});
  }, [searchParams, isSubmitting]);

  const handleSubmit = async () => {
    if (!data || !pqId || !hasChanges) return;

    const nextFieldErrors = {
      availableQuantity: !availableQuantity,
      availableFromDate:
        availabilityType !== AvailabilityTypeEnum.ALWAYS_IN_STOCK && !availableFromDate,
      leadTime: false,
      moq: !moq,
      price: !price,
      validUntilDate: !validUntilDate,
      incoterm: !incoterm,
      incotermLocation: !incotermLocation,
    };

    setFieldErrors(nextFieldErrors);

    if (Object.values(nextFieldErrors).some(Boolean)) {
      setUpdateShakeTrigger((prev) => prev + 1);
      return;
    }

    const quantityNum = parseInt(availableQuantity, 10);
    const moqNum = parseInt(moq, 10);
    if (isNaN(quantityNum) || isNaN(moqNum) || moqNum < 0 || quantityNum < 0) {
      setError(translate("single-product-offering.error.quantities-invalid"));
      return;
    }

    const up = data.units_per_pallet ?? 0;
    const uc = data.units_per_container ?? 0;

    let maximumAvailableQuantity: number;
    if (availableQuantityUnit === UnitTypeEnum.pallets) {
      if (up <= 0) {
        setError(translate("single-product-offering.error.units-per-pallet-missing"));
        return;
      }
      maximumAvailableQuantity = up * quantityNum;
    } else if (availableQuantityUnit === UnitTypeEnum.containers) {
      if (uc <= 0) {
        setError(translate("single-product-offering.error.units-per-container-missing"));
        return;
      }
      maximumAvailableQuantity = uc * quantityNum;
    } else {
      maximumAvailableQuantity = quantityNum;
    }

    let minimumOrderQuantity: number;
    if (moqUnit === UnitTypeEnum.pallets) {
      if (up <= 0) {
        setError(translate("single-product-offering.error.units-per-pallet-missing"));
        return;
      }
      minimumOrderQuantity = moqNum * up;
    } else if (moqUnit === UnitTypeEnum.containers) {
      if (uc <= 0) {
        setError(translate("single-product-offering.error.units-per-container-missing"));
        return;
      }
      minimumOrderQuantity = moqNum * uc;
    } else {
      minimumOrderQuantity = moqNum;
    }

    if (minimumOrderQuantity > maximumAvailableQuantity) {
      setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: true }));
      return;
    }

    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum < 0) {
      setError(translate("single-product-offering.error.price-invalid"));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const result = await updateSingleOffering(pqId, patchPayload);

      if (result.success) {
        captureEvent("stock_offer_updated", {
          offer_id: pqId,
          product_name: data.name ?? null,
          updated_fields: Object.keys(patchPayload).join(", "),
          ...(patchPayload as Record<string, string | number | boolean | null | undefined>),
        });
        handleClose();
        const params = new URLSearchParams(window.location.search);
        params.set(OFFER_UPDATED, "true");
        updateSearchParams(params);
        window.location.reload();
      } else {
        setError(result.error || translate("single-product-offering.error.create-failed"));
      }
    } catch (err) {
      setError(translate("single-product-offering.error.create-failed"));
      console.error("Error updating offering:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!shouldOpen) return null;

  const productLike = data
    ? {
      units_per_pallet: data.units_per_pallet ?? 0,
      units_per_container: data.units_per_container ?? 0,
    }
    : null;

  return (
    <Modal
      isOpen={shouldOpen}
      closeModal={handleClose}
      outsideClick={!isSubmitting}
      size="lg"
      modalContainerClassname="w-full max-w-[98vw] sm:max-w-[640px] md:max-w-[700px] lg:max-w-[800px] max-h-[90vh] flex flex-col"
      classname="max-w-full! flex-1 flex flex-col overflow-hidden md:pb-6"
      childrenDivWrapperClassName="flex-1 flex flex-col overflow-hidden"
    >
      <div className="relative w-full h-full flex flex-col min-h-0">
        {/* Scrollable content */}
        <Flex
          className={twMerge(
            "flex-col gap-8 w-full mx-auto flex-1 overflow-y-auto min-h-0",
            (loading || isSubmitting) && "opacity-70 pointer-events-none"
          )}
        >
          {loading && (
            <Flex className="flex-1 items-center justify-center py-12">
              <Icon name={IconName.Loader} size={spacing[10]} color={colors.gray[500]} className="animate-spin" />
            </Flex>
          )}

          {error && !loading && (
            <Flex className="bg-red-50 border border-red-200 rounded-lg p-3 shrink-0">
              <Text className="text-sm text-red-700 wrap-break-word">{error}</Text>
            </Flex>
          )}

          {data && !loading && (
            <Flex className="flex-col gap-6 shrink-0">
              {/* Product Details */}
              <Flex className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex-col gap-4">
                <Flex className="items-center gap-4">
                  <ProductImage
                    imageUrl={data.image_url || undefined}
                    width={80}
                    height={80}
                    className="w-20 h-20 object-contain rounded bg-white shrink-0"
                  />
                  <Flex className="flex-col gap-1 flex-1 justify-center min-w-0">
                    <Text className="text-base font-bold text-gray-900">{data.sf_product?.name}</Text>
                    {(data.product_model_series || data.product_module_surface_type || data.product_glass_type) && (
                      <Text className="text-sm text-gray-600">
                        {[
                          data.product_model_series,
                          data.product_module_surface_type,
                          data.product_glass_type,
                        ]
                          .filter(Boolean)
                          .join(" • ")}
                      </Text>
                    )}
                  </Flex>
                </Flex>
              </Flex>

              {/* Offer Details */}
              <Flex className="flex-col gap-6 border border-gray-200 rounded-lg p-4">
                <CollapsibleSection
                  title={translate("single-product-offering.offer-details")}
                  defaultExpanded={true}
                  contentClassName="gap-6"
                >
                  <Flex className="flex-col gap-4 pt-4">
                    {/* Row 1: Available Quantity */}
                    <Flex className="flex-col gap-2">
                      <Text variant="span" fontWeight="medium" color="black">
                        {translate("single-product-offering.available-quantity")} *
                      </Text>
                      <Flex className="items-start gap-2">
                        <Dropdown
                          name="quantity-unit"
                          options={[
                            { value: UnitTypeEnum.pieces, label: translate("single-product-offering.pieces") },
                            { value: UnitTypeEnum.pallets, label: translate("single-product-offering.pallets") },
                            { value: UnitTypeEnum.containers, label: translate("single-product-offering.containers") },
                          ]}
                          value={availableQuantityUnit}
                          onChange={(value) => setAvailableQuantityUnit(value as UnitTypeEnum)}
                          classNames="flex-1 min-w-0"
                          useRelativePositioning={true}
                        />
                        <NumberInput
                          name="available-quantity"
                          value={availableQuantity}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || (!isNaN(Number(val)) && Number(val) >= 0)) {
                              setAvailableQuantity(val);
                              setFieldErrors((prev) => ({ ...prev, availableQuantity: false, moqExceedsQuantity: false }));
                            }
                          }}
                          placeholder="Quantity"
                          classNames={twMerge("flex-1 min-w-0", fieldErrors.availableQuantity && "border-red-500")}
                        />
                        <Flex className="flex-col gap-2 mt-[-24px] flex-1 min-w-0">
                          <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.pieces")}</Text>
                          <TextInput
                            name="available-quantity-pieces"
                            value={
                              availableQuantity && !isNaN(Number(availableQuantity)) && Number(availableQuantity) > 0
                                ? availableQuantityUnit === UnitTypeEnum.pieces
                                  ? availableQuantity
                                  : availableQuantityUnit === UnitTypeEnum.pallets && (data?.units_per_pallet ?? 0) > 0
                                    ? (Number(availableQuantity) * (data.units_per_pallet ?? 0)).toString()
                                    : availableQuantityUnit === UnitTypeEnum.containers && (data?.units_per_container ?? 0) > 0
                                      ? (Number(availableQuantity) * (data.units_per_container ?? 0)).toString()
                                      : ""
                                : ""
                            }
                            isDisabled={true}
                            classNames="w-full"
                          />
                        </Flex>
                      </Flex>
                      {fieldErrors.availableQuantity && (
                        <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                          {getRequiredFieldError(translate("single-product-offering.available-quantity"))}
                        </Text>
                      )}
                    </Flex>

                    {/* Row 2: Available Time Period */}
                    <Flex className="flex-col gap-2">
                      <Text variant="span" fontWeight="medium" color="black">
                        {translate("single-product-offering.available-time-period")} *
                      </Text>
                      <Flex className="items-center gap-2">
                        <Dropdown
                          name="availability-type"
                          options={[
                            { value: AvailabilityTypeEnum.ALWAYS_IN_STOCK, label: translate("single-product-offering.always-in-stock") },
                            { value: AvailabilityTypeEnum.UPCOMING_STOCK, label: translate("single-product-offering.upcoming-stock") },
                            { value: AvailabilityTypeEnum.IN_STOCK_NOW, label: translate("single-product-offering.in-stock-now") },
                          ]}
                          value={availabilityType}
                          onChange={(value) => {
                            const newType = value as AvailabilityTypeEnum;
                            setAvailabilityType(newType);
                            if (newType === AvailabilityTypeEnum.IN_STOCK_NOW) {
                              setAvailableFromDate(new Date());
                              setFieldErrors((prev) => ({ ...prev, availableFromDate: false }));
                            } else if (newType === AvailabilityTypeEnum.UPCOMING_STOCK) {
                              setAvailableFromDate(null);
                            } else {
                              setAvailableFromDate(null);
                              setFieldErrors((prev) => ({ ...prev, availableFromDate: false }));
                            }
                          }}
                          classNames="flex-1 min-w-0"
                          useRelativePositioning={true}
                        />
                        {/* Date picker — only for date-based types */}
                        {availabilityType !== AvailabilityTypeEnum.ALWAYS_IN_STOCK && (
                          <DatePickerButton
                            value={availableFromDate || undefined}
                            onChange={(date) => {
                              setAvailableFromDate(date);
                              setFieldErrors((prev) => ({ ...prev, availableFromDate: false }));
                            }}
                            placeholder="dd.mm.yyyy"
                            classNames={twMerge("flex-1 min-w-0", fieldErrors.availableFromDate && "border-red-500")}
                            uniqueQueryId="update-availability-date"
                            isDisabled={availabilityType === AvailabilityTypeEnum.IN_STOCK_NOW}
                          />
                        )}
                        {/* Lead time — always visible, optional (sends 0 when empty) */}
                        <NumberInput
                          name="lead-time"
                          value={leadTime}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || (!isNaN(Number(val)) && Number(val) >= 0)) {
                              setLeadTime(val);
                            }
                          }}
                          placeholder={translate("single-product-offering.preparation-lead-time")}
                          classNames="flex-1 min-w-0"
                        />
                      </Flex>
                      {fieldErrors.availableFromDate && availabilityType !== AvailabilityTypeEnum.ALWAYS_IN_STOCK && (
                        <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                          {getRequiredFieldError(translate("single-product-offering.earliest-availability-date"))}
                        </Text>
                      )}
                    </Flex>
                  </Flex>

                  <HorizontalLine />

                  {/* UoM Conversion Ratio - read only */}
                  {productLike &&
                    (productLike.units_per_pallet > 0 || productLike.units_per_container > 0) && (
                      <Flex className="flex-col gap-3 border-gray-200">
                        <Text fontWeight="semibold" color="lightGray">
                          {translate("single-product-offering.uom-conversion-ratio")}
                        </Text>
                        <Flex className="items-end gap-4">
                          {productLike.units_per_container > 0 && (
                            <Flex className="items-end gap-3 flex-1">
                              <Flex className="flex-col gap-2 flex-1">
                                <Text variant="span" fontWeight="medium" color="lightGray">
                                  {translate("single-product-offering.containers")}
                                </Text>
                                <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                  <Icon name={IconName.Container} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                  <Text variant="div" className="text-gray-400">1</Text>
                                </div>
                              </Flex>
                              <Text variant="div" className="text-gray-600 mb-2">=</Text>
                              <Flex className="flex-col gap-2 flex-1">
                                <Text variant="span" fontWeight="medium" color="lightGray">
                                  {translate("single-product-offering.pieces")}
                                </Text>
                                <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                  <Icon name={IconName.Pieces} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                  <Text variant="div" className="text-gray-400">{productLike.units_per_container}</Text>
                                </div>
                              </Flex>
                            </Flex>
                          )}
                          {productLike.units_per_container > 0 && productLike.units_per_pallet > 0 && (
                            <VerticalLine className="h-12 md:h-16 bg-gray-200" />
                          )}
                          {productLike.units_per_pallet > 0 && (
                            <Flex className="items-end gap-3 flex-1">
                              <Flex className="flex-col gap-2 flex-1">
                                <Text variant="span" fontWeight="medium" color="lightGray">
                                  {translate("single-product-offering.pallets")}
                                </Text>
                                <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                  <Icon name={IconName.Pallets} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                  <Text variant="div" className="text-gray-400">1</Text>
                                </div>
                              </Flex>
                              <Text variant="div" className="text-gray-600 mb-2">=</Text>
                              <Flex className="flex-col gap-2 flex-1">
                                <Text variant="span" fontWeight="medium" color="lightGray">
                                  {translate("single-product-offering.pieces")}
                                </Text>
                                <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                  <Icon name={IconName.Pieces} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                  <Text variant="div" className="text-gray-400">{productLike.units_per_pallet}</Text>
                                </div>
                              </Flex>
                            </Flex>
                          )}
                          {productLike.units_per_container > 0 && productLike.units_per_pallet > 0 && (
                            <>
                              <VerticalLine className="h-12 md:h-16 bg-gray-200" />
                              <Flex className="items-end gap-3 flex-1">
                                <Flex className="flex-col gap-2 flex-1">
                                  <Text variant="span" fontWeight="medium" color="lightGray">
                                    {translate("single-product-offering.containers")}
                                  </Text>
                                  <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                    <Icon name={IconName.Container} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                    <Text variant="div" className="text-gray-400">1</Text>
                                  </div>
                                </Flex>
                                <Text variant="div" className="text-gray-600 mb-2">=</Text>
                                <Flex className="flex-col gap-2 flex-1">
                                  <Text variant="span" fontWeight="medium" color="lightGray">
                                    {translate("single-product-offering.pallets")}
                                  </Text>
                                  <div className="flex-1 border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                    <Icon name={IconName.Pallets} size={spacing[4]} customStrokeWidth={2} color={colors.gray[400]} />
                                    <Text variant="div" className="text-gray-400">
                                      {Math.round((productLike.units_per_container / productLike.units_per_pallet) * 100) / 100}
                                    </Text>
                                  </div>
                                </Flex>
                              </Flex>
                            </>
                          )}
                        </Flex>
                      </Flex>
                    )}

                  <HorizontalLine />

                  {/* MOQ */}
                  <Flex className="flex-col gap-4">
                    <Text fontWeight="semibold" color="black">{translate("pq-ingestion.moq")} *</Text>
                    <Flex className="flex-col gap-3">
                      <Flex className="items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer" onClick={() => { setMoqUnit(UnitTypeEnum.pieces); setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false })); }}>
                          <button type="button" onClick={(e) => { e.preventDefault(); setMoqUnit(UnitTypeEnum.pieces); setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false })); }} className="flex items-center justify-center">
                            <Icon name={moqUnit === UnitTypeEnum.pieces ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked} size={spacing[5]} color={moqUnit === UnitTypeEnum.pieces ? colors.red[600] : colors.gray[400]} className="cursor-pointer" />
                          </button>
                          <Icon name={IconName.Pieces} size={spacing[5]} customStrokeWidth={2} color={colors.gray[900]} />
                          <Text className="text-sm text-gray-900">{translate("single-product-offering.pieces")}</Text>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer" onClick={() => { setMoqUnit(UnitTypeEnum.pallets); setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false })); }}>
                          <button type="button" onClick={(e) => { e.preventDefault(); setMoqUnit(UnitTypeEnum.pallets); setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false })); }} className="flex items-center justify-center">
                            <Icon name={moqUnit === UnitTypeEnum.pallets ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked} size={spacing[5]} color={moqUnit === UnitTypeEnum.pallets ? colors.red[600] : colors.gray[400]} className="cursor-pointer" />
                          </button>
                          <Icon name={IconName.Pallets} size={spacing[5]} customStrokeWidth={2} color={colors.gray[900]} />
                          <Text className="text-sm text-gray-900">{translate("single-product-offering.full-pallets-only")}</Text>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer" onClick={() => { setMoqUnit(UnitTypeEnum.containers); setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false })); }}>
                          <button type="button" onClick={(e) => { e.preventDefault(); setMoqUnit(UnitTypeEnum.containers); setFieldErrors((prev) => ({ ...prev, moqExceedsQuantity: false })); }} className="flex items-center justify-center">
                            <Icon name={moqUnit === UnitTypeEnum.containers ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked} size={spacing[5]} color={moqUnit === UnitTypeEnum.containers ? colors.red[600] : colors.gray[400]} className="cursor-pointer" />
                          </button>
                          <Icon name={IconName.Container} size={spacing[5]} customStrokeWidth={2} color={colors.gray[900]} />
                          <Text className="text-sm text-gray-900">{translate("single-product-offering.full-containers-only")}</Text>
                        </label>
                      </Flex>
                      <Flex className="gap-4">
                        <Flex className="flex-col gap-2">
                          <Text variant="span" fontWeight="medium" color="black">{translate("single-product-offering.min-order-quantity")} *</Text>
                          <NumberInput
                            name="moq"
                            value={moq}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "" || (!isNaN(Number(val)) && Number(val) >= 0)) {
                                setMoq(val);
                                setFieldErrors((prev) => ({ ...prev, moq: false, moqExceedsQuantity: false }));
                              }
                            }}
                            placeholder="Quantity"
                            classNames={twMerge(moqUnit === UnitTypeEnum.pieces ? "w-full" : "flex-1", (fieldErrors.moq || fieldErrors.moqExceedsQuantity) && "border-red-500")}
                          />
                          {fieldErrors.moq && (
                            <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                              {getRequiredFieldError(translate("single-product-offering.min-order-quantity"))}
                            </Text>
                          )}
                        </Flex>
                        <Flex className="flex-col gap-2">
                          <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.pieces")}</Text>
                          <TextInput
                            name="moq-pieces"
                            value={
                              moq && !isNaN(Number(moq)) && Number(moq) > 0
                                ? moqUnit === UnitTypeEnum.pieces
                                  ? moq
                                  : moqUnit === UnitTypeEnum.pallets && (productLike?.units_per_pallet ?? 0) > 0
                                    ? (Number(moq) * (productLike?.units_per_pallet ?? 0)).toString()
                                    : moqUnit === UnitTypeEnum.containers && (productLike?.units_per_container ?? 0) > 0
                                      ? (Number(moq) * (productLike?.units_per_container ?? 0)).toString()
                                      : ""
                                : ""
                            }
                            isDisabled={true}
                            classNames={twMerge("w-24", fieldErrors.moqExceedsQuantity && "border-red-500")}
                          />
                          {fieldErrors.moqExceedsQuantity && (
                            <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                              {translate("single-product-offering.error.moq-exceeds-quantity")}
                            </Text>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>

                  <HorizontalLine />

                  {/* Pricing */}
                  <Flex className="flex-col gap-4">
                    <Text fontWeight="semibold" color="black">{translate("single-product-offering.pricing")} *</Text>
                    <Flex className="items-center gap-6">
                      <Flex className="flex-col gap-3">
                        <Flex className="items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer" onClick={() => setPriceUnit(PriceUnitEnum.pieces)}>
                            <button type="button" onClick={(e) => { e.preventDefault(); setPriceUnit(PriceUnitEnum.pieces); }} className="flex items-center justify-center">
                              <Icon name={priceUnit === PriceUnitEnum.pieces ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked} size={spacing[5]} color={priceUnit === PriceUnitEnum.pieces ? colors.red[600] : colors.gray[400]} className="cursor-pointer" />
                            </button>
                            <Text className="text-sm text-gray-700">{translate("single-product-offering.price-per-pieces")}</Text>
                          </label>
                          {data?.category === ProductCategory.Modules && (
                            <label className="flex items-center gap-2 cursor-pointer" onClick={() => setPriceUnit(PriceUnitEnum.wattpeak)}>
                              <button type="button" onClick={(e) => { e.preventDefault(); setPriceUnit(PriceUnitEnum.wattpeak); }} className="flex items-center justify-center">
                                <Icon name={priceUnit === PriceUnitEnum.wattpeak ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked} size={spacing[5]} color={priceUnit === PriceUnitEnum.wattpeak ? colors.red[600] : colors.gray[400]} className="cursor-pointer" />
                              </button>
                              <Text className="text-sm text-gray-700">{translate("single-product-offering.price-per-wattpeak")}</Text>
                            </label>
                          )}
                        </Flex>
                        <Flex className="items-center gap-2">
                          <NumberInput
                            name="price"
                            value={price}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "" || (!isNaN(Number(val)) && Number(val) >= 0)) {
                                setPrice(val);
                                setFieldErrors((prev) => ({ ...prev, price: false }));
                              }
                            }}
                            placeholder="Price"
                            classNames={twMerge("flex-1", fieldErrors.price && "border-red-500")}
                          />
                          <TextInput name="currency" value={translate("single-product-offering.currency")} isDisabled={true} classNames="w-24" />
                        </Flex>
                        {fieldErrors.price && (
                          <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                            {getRequiredFieldError(translate("single-product-offering.pricing"))}
                          </Text>
                        )}
                      </Flex>
                      <VerticalLine className="h-12 md:h-16 bg-gray-200" />
                      <Flex className="flex-col gap-3">
                        <Text variant="span" fontWeight="medium" color="black">{translate("single-product-offering.offer-valid-until")} *</Text>
                        <DatePickerButton
                          value={validUntilDate || undefined}
                          onChange={(date) => {
                            setValidUntilDate(date);
                            setFieldErrors((prev) => ({ ...prev, validUntilDate: false }));
                          }}
                          placeholder="dd.mm.yyyy"
                          classNames={twMerge("w-full", fieldErrors.validUntilDate && "border-red-500")}
                          uniqueQueryId="update-valid-until-date"
                        />
                        {fieldErrors.validUntilDate && (
                          <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                            {getRequiredFieldError(translate("single-product-offering.offer-valid-until"))}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  </Flex>
                </CollapsibleSection>
              </Flex>

              {/* Handover */}
              <Flex className="flex-col gap-6 border border-gray-200 rounded-lg p-4 mb-4">
                <CollapsibleSection
                  title={translate("single-product-offering.handover")}
                  defaultExpanded={true}
                  contentClassName="gap-6"
                >
                  <Flex className="flex-col gap-2 pt-4">
                    <Text variant="span" fontWeight="medium" color="black">{translate("single-product-offering.handover-location")} *</Text>
                    <IncotermLocationSelect
                      name="handover-location"
                      trackingContext="single_offer_update"
                      value={incotermLocation}
                      onChange={(location) => {
                        setIncotermLocation(location);
                        setFieldErrors((prev) => ({ ...prev, incotermLocation: false }));
                      }}
                      error={fieldErrors.incotermLocation}
                    />
                    {fieldErrors.incotermLocation && (
                      <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                        {getRequiredFieldError(translate("single-product-offering.handover-location"))}
                      </Text>
                    )}
                  </Flex>
                  <HorizontalLine />
                  <Flex className="flex-col gap-4">
                    <Text fontWeight="semibold" color="lightGray">{translate("single-product-offering.available-pickup-options")}</Text>
                    <Flex className="items-center gap-4">
                      <Flex className="flex-col gap-2">
                        <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.select-incoterm")} *</Text>
                        <Flex className={twMerge("items-center gap-3 flex-wrap rounded-md", fieldErrors.incoterm && "border border-red-500 p-2")}>
                          {INCOTERM_OPTIONS.map((option) => (
                            <label key={option.value} className="flex items-center gap-2 cursor-pointer" onClick={() => setIncoterm(option.value)}>
                              <button type="button" onClick={(e) => { e.preventDefault(); setIncoterm(option.value); setFieldErrors((prev) => ({ ...prev, incoterm: false })); }} className="flex items-center justify-center">
                                <Icon name={incoterm === option.value ? IconName.RadioButtonChecked : IconName.RadioButtonUnchecked} size={spacing[5]} color={incoterm === option.value ? colors.red[600] : colors.gray[400]} className="cursor-pointer" />
                              </button>
                              <Text className="text-[10px] md:text-xs text-gray-900">{option.label}</Text>
                            </label>
                          ))}
                        </Flex>
                        {fieldErrors.incoterm && (
                          <Text className="text-[8px] md:text-[10px] font-medium text-red-600">
                            {getRequiredFieldError(translate("single-product-offering.select-incoterm"))}
                          </Text>
                        )}
                      </Flex>
                      <VerticalLine className="h-12 md:h-16 bg-gray-200" />
                      <Flex className="flex-col gap-2">
                        <Text variant="span" fontWeight="medium" color="lightGray">{translate("single-product-offering.included-logistic-cost")}</Text>
                        <Flex className="items-center gap-3">
                          <Switch
                            checked={includedLogisticCost}
                            onChange={(e) => {
                              e.preventDefault();
                              setIncludedLogisticCost(!includedLogisticCost);
                            }}
                          />
                          <Text className="text-sm text-gray-600">
                            {includedLogisticCost ? translate("single-product-offering.yes") : translate("single-product-offering.no")}
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>
                </CollapsibleSection>
              </Flex>
            </Flex>
          )}
        </Flex>

        {/* Fixed footer - Action Buttons (visible at all times) */}
        {!loading && data && (
          <Flex className="gap-3 justify-between w-full pt-4 shrink-0">
            <Button
              buttonStyle={ButtonTypesEnum.default}
              text={translate("general.cancel")}
              onClick={handleClose}
              buttonClassName="px-6 py-2 border border-gray-300 w-fit"
              disabled={isSubmitting}
            />
            <Flex className="items-center gap-2">
              <Button
                buttonStyle={ButtonTypesEnum.primary}
                text={translate("update-product-offering.update-offer")}
                onClick={handleSubmit}
                disabled={!hasChanges || isSubmitting}
                isLoading={isSubmitting}
                triggerShake={updateShakeTrigger}
                buttonClassName="px-6 py-2 w-fit"
              />
            </Flex>
          </Flex>
        )}

        {isSubmitting && (
          <div className="absolute inset-0 z-70 flex items-center justify-center pointer-events-none">
            <AndercoreLogoLoadingGif height={84} width={84} className="h-full bg-transparent" />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UpdateSingleOfferModal;
